import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { canUseApsChannelAnalysis } from '../_helpers/featureAccess';
import {
  ApsDistributionResponse,
  ApsHistogramResponse,
  ApsSerial,
  downloadApsCsv,
  getApsDistribution,
  getApsHistogram,
  getApsSerials
} from '../services/aps-channel.service';
import BinContourPlot from '../components/ParticleBins/BinContourPlot';
import BinBoxPlot from '../components/ParticleBins/BinBoxPlot';
import BinBoundariesControl, {
  BoundariesState,
  BoundaryConfig,
  loadStoredBoundaries
} from '../components/ParticleBins/BinBoundariesControl';
import {
  buildBinDataset,
  buildBinPrompt,
  downloadSizeDistribution
} from '../components/ParticleBins/particleBinExport';
import {
  APS_AEROSOL_FLOW_CM3_PER_S,
  APS_BOUNDARIES_STORAGE_KEY,
  APS_CHANNEL_COUNT,
  APS_CONC_UNIT,
  APS_RATE_UNIT,
  DEFAULT_APS_BOUNDARIES
} from '../components/ParticleBins/particleBinShared';
import { stashChartAiHandoff } from '../services/chart-ai-handoff';
import { formatBytes } from '../services/streaming-download';
import Loader from '../components/loader';

/** APS-3321 channel boundaries, overridable by spreadsheet upload. */
const APS_BOUNDARY_CONFIG: BoundaryConfig = {
  binCount: APS_CHANNEL_COUNT,
  defaults: DEFAULT_APS_BOUNDARIES,
  storageKey: APS_BOUNDARIES_STORAGE_KEY,
  instrument: 'APS-3321',
  binWord: 'channel'
};

/**
 * Y window for the contour plot. The APS measures aerodynamic diameter from its
 * 0.523 µm under-range bin up to 19.81 µm, so the axis covers the whole range
 * rather than the OPC's narrow fine-particle window.
 */
const Y_MIN_UM = 0.3;
const Y_MAX_UM = 20;
const Y_TICKS_UM = [0.3, 0.5, 1, 2, 5, 10, 20];

const PRESETS: { key: string; label: string; ms: number }[] = [
  { key: '1h', label: '1h', ms: 3600_000 },
  { key: '6h', label: '6h', ms: 6 * 3600_000 },
  { key: '24h', label: '24h', ms: 86400_000 },
  { key: '7d', label: '7d', ms: 7 * 86400_000 },
  { key: '30d', label: '30d', ms: 30 * 86400_000 }
];

const INTERVALS = ['auto', '20s', '1m', '5m', '15m', '1h', '6h', '1d'];

/** As in the OPC page: too few buckets to contour, so let the server choose. */
const MIN_BUCKETS = 3;

const UNIT_MS: Record<string, number> = { s: 1000, m: 60_000, h: 3600_000, d: 86400_000 };

/**
 * Parse an interval like "15m" into milliseconds.
 * @param interval - Interval string.
 * @returns Milliseconds, or null when the string is not a fixed interval.
 */
function intervalToMs(interval: string): number | null {
  const m = /^(\d+)([smhd])$/.exec(interval);
  if (!m) return null;
  return parseInt(m[1], 10) * UNIT_MS[m[2]];
}

/**
 * Format a millis timestamp as a datetime-local input value.
 * @param ms - Epoch millis.
 * @returns "YYYY-MM-DDTHH:mm" in local time.
 */
function toLocalInput(ms: number): string {
  const d = new Date(ms);
  /**
   * Zero-pad to two digits.
   * @param n - Value.
   * @returns Padded string.
   */
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Short description of an instrument's data extent, for the picker.
 * @param s - Serial record.
 * @returns Label like "72551001 · drecord · Jul 24 – Jul 28".
 */
function serialLabel(s: ApsSerial): string {
  /**
   * Render one end of the extent.
   * @param ms - Epoch millis, or null when absent.
   * @returns Short date, or "?".
   */
  const day = (ms: number | null): string =>
    ms === null ? '?' : new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${s.serial} · ${s.source} · ${day(s.firstSample)} – ${day(s.lastSample)}`;
}

/**
 * TSI APS channel analysis: contour plot and size-distribution box plot over the
 * 52 aerodynamic-diameter channels, mirroring the OPC-N3 bin page.
 * @returns The APS channels page.
 */
export default function ApsChannelsPage(): ReactElement {
  const isAllowed = canUseApsChannelAnalysis();
  const navigate = useNavigate();

  const [serials, setSerials] = useState<ApsSerial[]>([]);
  const [serial, setSerial] = useState('');
  // Every APS record predates the usual presets, so the window defaults to the
  // selected instrument's own data extent instead of "last 7 days".
  const [preset, setPreset] = useState('full');
  const [customStart, setCustomStart] = useState(toLocalInput(Date.now() - 86400_000));
  const [customEnd, setCustomEnd] = useState(toLocalInput(Date.now()));
  const [interval, setInterval] = useState('auto');
  const [asConcentration, setAsConcentration] = useState(false);

  const [histogram, setHistogram] = useState<ApsHistogramResponse | null>(null);
  const [distribution, setDistribution] = useState<ApsDistributionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportBytes, setExportBytes] = useState(0);
  const [boundaries, setBoundaries] = useState<BoundariesState>(() =>
    loadStoredBoundaries(APS_BOUNDARY_CONFIG)
  );
  const [sliceTs, setSliceTs] = useState<number[]>([]);

  const selected = useMemo(
    () => serials.find((s) => s.serial === serial) ?? null,
    [serials, serial]
  );

  /**
   * Resolve the effective [start, end] window. "full" spans the selected
   * instrument's records, padded by a second so the last sample is included.
   * @param instrument - The instrument whose extent "full" refers to.
   * @returns Window in epoch millis, or null when it cannot be resolved.
   */
  const resolveRange = useCallback(
    (instrument: ApsSerial | null): { start: number; end: number } | null => {
      if (preset === 'full') {
        if (!instrument || instrument.firstSample === null || instrument.lastSample === null) {
          toast.error('That instrument has no dated records.');
          return null;
        }
        return { start: instrument.firstSample, end: instrument.lastSample + 1000 };
      }
      if (preset !== 'custom') {
        const ms = PRESETS.find((p) => p.key === preset)?.ms ?? 86400_000;
        const end = Date.now();
        return { start: end - ms, end };
      }
      const start = new Date(customStart).getTime();
      const end = new Date(customEnd).getTime();
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
        toast.error('Custom range is invalid: end must be after start.');
        return null;
      }
      return { start, end };
    },
    [preset, customStart, customEnd]
  );

  const fetchData = useCallback(
    async (forSerial?: string, instrument?: ApsSerial | null): Promise<void> => {
      const target = (forSerial ?? serial).trim();
      if (!target) {
        toast.error('Pick an instrument first.');
        return;
      }
      const range = resolveRange(instrument !== undefined ? instrument : selected);
      if (!range) return;

      const fixedMs = interval === 'auto' ? null : intervalToMs(interval);
      const tooCoarse = fixedMs !== null && (range.end - range.start) / fixedMs < MIN_BUCKETS;
      const effectiveInterval = interval === 'auto' || tooCoarse ? undefined : interval;

      setLoading(true);
      try {
        const [histRes, distRes] = await Promise.all([
          getApsHistogram(target, range.start, range.end, effectiveInterval),
          getApsDistribution(target, range.start, range.end)
        ]);
        setHistogram(histRes.data);
        setDistribution(distRes.data);
        // Bucket indexes are renumbered by every fetch.
        setSliceTs([]);
        if (histRes.data.timestamps.length === 0) {
          toast.info(`No APS data for ${target} in the selected range.`);
        }
      } catch (e: unknown) {
        const message =
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Failed to load APS channel data.';
        toast.error(message);
      } finally {
        setLoading(false);
      }
    },
    [serial, selected, interval, resolveRange]
  );

  // Populate the picker, then load the instrument with the most recent data.
  useEffect(() => {
    if (!isAllowed) return;
    getApsSerials()
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setSerials(list);
        if (list.length > 0) {
          setSerial(list[0].serial);
          fetchData(list[0].serial, list[0]);
        }
      })
      .catch(() => {
        toast.error('Could not list APS instruments.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Count rates are what the API returns; a number concentration needs the
   * instrument's aerosol flow, which is an assumption rather than a measurement.
   */
  const scale = asConcentration ? 1 / APS_AEROSOL_FLOW_CM3_PER_S : 1;
  const unit = asConcentration ? APS_CONC_UNIT : APS_RATE_UNIT;

  const scaledValues = useMemo(() => {
    if (!histogram) return [];
    if (scale === 1) return histogram.values;
    return histogram.values.map((row) => row.map((v) => (v === null ? null : v * scale)));
  }, [histogram, scale]);

  const scaledStats = useMemo(() => {
    if (!distribution) return [];
    if (scale === 1) return distribution.channels;
    /**
     * Scale one statistic, preserving nulls.
     * @param v - Value or null.
     * @returns Scaled value.
     */
    const s = (v: number | null): number | null => (v === null ? null : v * scale);
    return distribution.channels.map((c) => ({
      ...c,
      min: s(c.min),
      max: s(c.max),
      mean: s(c.mean),
      p05: s(c.p05),
      p25: s(c.p25),
      p50: s(c.p50),
      p75: s(c.p75),
      p95: s(c.p95)
    }));
  }, [distribution, scale]);

  const exportMeta = useMemo(
    () =>
      histogram
        ? {
            serial: histogram.serial,
            startDate: histogram.startDate,
            endDate: histogram.endDate,
            interval: histogram.interval,
            boundaries: boundaries.edges,
            binCount: APS_CHANNEL_COUNT,
            unit,
            instrument: 'TSI APS-3321',
            filePrefix: 'aps'
          }
        : null,
    [histogram, boundaries.edges, unit]
  );

  const selectedSlices = useMemo(
    () =>
      histogram
        ? sliceTs
            .filter((t) => t >= 0 && t < scaledValues.length)
            .sort((a, b) => a - b)
            .map((t) => ({ timestamp: histogram.timestamps[t], values: scaledValues[t] }))
        : [],
    [sliceTs, histogram, scaledValues]
  );

  /** Download every complete sample in the window as raw counts. */
  const exportRaw = useCallback(async (): Promise<void> => {
    if (!exportMeta) return;
    setExporting(true);
    setExportBytes(0);
    try {
      await downloadApsCsv(
        String(exportMeta.serial),
        exportMeta.startDate,
        exportMeta.endDate,
        setExportBytes
      );
    } catch (e: unknown) {
      toast.error((e as Error)?.message ?? 'Failed to export APS data.');
    } finally {
      setExporting(false);
    }
  }, [exportMeta]);

  if (!isAllowed) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="relative flex flex-1 flex-col gap-4 px-6 py-6 text-gray-100">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold">APS Channel Analysis</h1>
          <p className="text-base text-gray-400">
            TSI APS-3321 aerodynamic size channels (1–52) · instrument{' '}
            {histogram?.serial ?? (serial || '—')}
            {histogram?.source ? ` · ${histogram.source}` : ''}
          </p>
        </div>
        <div className="flex gap-3 text-base">
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-2">
            <div className="text-sm text-gray-400">Samples</div>
            <div className="text-lg font-semibold">
              {distribution?.sampleCount?.toLocaleString() ?? '—'}
            </div>
          </div>
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-2">
            <div className="text-sm text-gray-400">Buckets</div>
            <div className="text-lg font-semibold">{histogram?.timestamps.length ?? '—'}</div>
          </div>
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-2">
            <div className="text-sm text-gray-400">Interval</div>
            <div className="text-lg font-semibold">{histogram?.interval ?? '—'}</div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-3 rounded-lg border border-gray-800 bg-container-500 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-base text-gray-400" htmlFor="aps-serial">
            Instrument
            <select
              id="aps-serial"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              className="rounded border border-gray-800 bg-background-700 px-3 py-1.5 text-base text-gray-100 focus:border-primary-500 focus:outline-none"
            >
              {serials.length === 0 && <option value="">No instruments found</option>}
              {serials.map((s) => (
                <option key={`${s.source}-${s.serial}`} value={s.serial}>
                  {serialLabel(s)}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPreset('full')}
              title="The whole extent of this instrument's records"
              className={`rounded border px-2.5 py-1.5 text-sm transition ${
                preset === 'full'
                  ? 'border-primary-500/60 bg-primary-500/20 text-primary-400'
                  : 'border-gray-800 bg-background-700 text-gray-400 hover:text-gray-200'
              }`}
            >
              Full range
            </button>
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPreset(p.key)}
                className={`rounded border px-2.5 py-1.5 text-sm transition ${
                  preset === p.key
                    ? 'border-primary-500/60 bg-primary-500/20 text-primary-400'
                    : 'border-gray-800 bg-background-700 text-gray-400 hover:text-gray-200'
                }`}
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPreset('custom')}
              className={`rounded border px-2.5 py-1.5 text-sm transition ${
                preset === 'custom'
                  ? 'border-primary-500/60 bg-primary-500/20 text-primary-400'
                  : 'border-gray-800 bg-background-700 text-gray-400 hover:text-gray-200'
              }`}
            >
              Custom
            </button>
          </div>
          {preset === 'custom' && (
            <div className="flex items-center gap-2 text-base text-gray-400">
              <input
                type="datetime-local"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="rounded border border-gray-800 bg-background-700 px-2 py-1.5 text-sm text-gray-100 focus:border-primary-500 focus:outline-none"
              />
              →
              <input
                type="datetime-local"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="rounded border border-gray-800 bg-background-700 px-2 py-1.5 text-sm text-gray-100 focus:border-primary-500 focus:outline-none"
              />
            </div>
          )}
          <label className="flex items-center gap-2 text-base text-gray-400" htmlFor="aps-interval">
            Interval
            <select
              id="aps-interval"
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              className="rounded border border-gray-800 bg-background-700 px-2 py-1.5 text-sm text-gray-100 focus:border-primary-500 focus:outline-none"
            >
              {INTERVALS.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => fetchData()}
            disabled={loading}
            className="flex items-center gap-1.5 rounded border border-primary-500/40 bg-primary-500/15 px-3 py-1.5 text-base text-primary-400 transition hover:bg-primary-500/25 disabled:opacity-50"
          >
            <span className="material-icons-outlined text-lg">refresh</span>
            Apply
          </button>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={exportRaw}
              disabled={!exportMeta || exporting}
              title="Every complete sample in the window as raw counts (CSV)"
              className="flex items-center gap-1.5 rounded border border-gray-800 bg-background-700 px-3 py-1.5 text-base text-gray-300 transition hover:text-gray-100 disabled:opacity-40"
            >
              <span className="material-icons-outlined text-lg">download</span>
              {exporting
                ? `Exporting… ${exportBytes > 0 ? formatBytes(exportBytes) : ''}`
                : 'All data'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (exportMeta && distribution) {
                  downloadSizeDistribution(
                    exportMeta,
                    scaledStats,
                    selectedSlices
                  );
                }
              }}
              disabled={!distribution}
              title={
                selectedSlices.length > 0
                  ? `Window average plus the ${selectedSlices.length} time(s) selected on the contour plot`
                  : 'Window average — click times on the contour plot to add them'
              }
              className="flex items-center gap-1.5 rounded border border-gray-800 bg-background-700 px-3 py-1.5 text-base text-gray-300 transition hover:text-gray-100 disabled:opacity-40"
            >
              <span className="material-icons-outlined text-lg">download</span>
              Size distribution
            </button>
            <button
              type="button"
              onClick={() => {
                if (!exportMeta || !histogram) return;
                stashChartAiHandoff({
                  dataset: buildBinDataset(exportMeta, histogram.timestamps, scaledValues),
                  prompt: buildBinPrompt(exportMeta)
                });
                navigate('/ai-agent');
              }}
              disabled={!histogram || histogram.timestamps.length === 0}
              className="flex items-center gap-1.5 rounded border border-primary-500/40 bg-primary-500/15 px-3 py-1.5 text-base text-primary-400 transition hover:bg-primary-500/25 disabled:opacity-40"
            >
              <span className="material-icons-outlined text-lg">auto_awesome</span>
              AI Insights
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <BinBoundariesControl
            boundaries={boundaries}
            onChange={setBoundaries}
            config={APS_BOUNDARY_CONFIG}
          />
          <label
            className="flex cursor-pointer select-none items-center gap-2 text-base text-gray-400"
            htmlFor="aps-units"
          >
            <input
              id="aps-units"
              type="checkbox"
              checked={asConcentration}
              onChange={(e) => setAsConcentration(e.target.checked)}
              className="h-[18px] w-[18px]"
            />
            Show as {APS_CONC_UNIT}
            <span
              className="material-icons-outlined text-lg text-gray-500"
              title={`Divides the count rate by the APS-3321's nominal 1.0 L/min aerosol flow (${APS_AEROSOL_FLOW_CM3_PER_S.toFixed(
                1
              )} cm³/s). The record carries no flow field, so this is the instrument's specification rather than a measured value — verify before treating it as calibrated.`}
            >
              info
            </span>
          </label>
        </div>
      </div>

      {loading && <Loader />}

      {histogram && (
        <BinContourPlot
          timestamps={histogram.timestamps}
          values={scaledValues}
          boundaries={boundaries.edges}
          sliceTs={sliceTs}
          onSlicesChange={setSliceTs}
          binCount={APS_CHANNEL_COUNT}
          unit={unit}
          yMinUm={Y_MIN_UM}
          yMaxUm={Y_MAX_UM}
          yTicksUm={Y_TICKS_UM}
          title="Channel Concentration Contours"
        />
      )}
      {distribution && (
        <BinBoxPlot
          stats={scaledStats}
          boundaries={boundaries.edges}
          overlays={selectedSlices}
          binCount={APS_CHANNEL_COUNT}
          unit={unit}
          subtitle={`Box plot of ${unit} per aerodynamic diameter over the selected period`}
        />
      )}

      {!loading && !histogram && (
        <p className="py-16 text-center text-base text-gray-500">
          Pick an instrument and time range, then Apply.
        </p>
      )}
    </div>
  );
}
